// Entering a company: POST users/select-company exchanges the loginToken for a
// company-scoped authToken, stores the session it returns, and lands the user in
// the app. Shared by the post-login picker (/select-company) and the in-app
// Switch company modal so the two can't drift.
//
// The RTK Query reset is the part that isn't obvious. store.ts resets the cache
// on `clearAuth`, which covers logout and a failed refresh — but a company
// switch keeps the user signed in, so that listener never fires and every cached
// list (people, reports, forms…) would be re-served from the PREVIOUS company.
// `setAuth` can't just be added to that listener either: axiosBaseQuery
// dispatches it on every silent 401 refresh, which would wipe the cache
// mid-session on a routine token rotation. So the reset is dispatched here,
// explicitly, where "the tenant changed" is actually known.
//
// It lands on '/' rather than the current route: App.tsx redirects '/' to
// firstAccessiblePath recomputed from the NEW company's permissions, so the user
// can't be left on a module this company doesn't grant.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useAppDispatch } from '@/store/hooks';
import { baseApi } from '@/api/baseApi';
import { useLazyGetOwnerDetailsQuery, useSelectCompanyMutation } from '@/api/endpoints/companyApi';
import { setAuth } from '@/redux/auth/authSlice';
import type { ApiErrorShape } from '@/api/types';

const useSelectCompany = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [selectCompany, { isLoading: selecting }] = useSelectCompanyMutation();
  const [getOwnerDetails] = useLazyGetOwnerDetailsQuery();
  const [pendingId, setPendingId] = useState<string | null>(null);

  /** `onDone` runs after the session is stored and before the cache reset — the
   *  modal closes there, so its company-list subscription is gone and the reset
   *  doesn't refetch behind a closing dialog. */
  const select = async (companyId: string, onDone?: () => void) => {
    if (selecting) return;
    setPendingId(companyId);
    try {
      const result = await selectCompany({ companyId }).unwrap();

      dispatch(
        setAuth({
          authToken: result.token,
          refreshToken: result.refreshToken,
          companyId: result.companyId,
          companyDetail: result.company,
          roles: result.roles,
          permissions: result.permissions,
          user: result.user,
          companyUserId: result.companyUserId,
          // setAuth merges, so wipe the previous company's owner up front — the
          // fetch below runs on the NEW authToken just stored and may still fail.
          ownerCompanyUserId: null,
        }),
      );
      try {
        const owner = await getOwnerDetails().unwrap();
        dispatch(setAuth({ ownerCompanyUserId: owner.companyUserId }));
      } catch {
        // Non-fatal: the tenant already switched (tokens stored), so failing the
        // whole flow here would strand the user half-switched.
      }
      onDone?.();
      dispatch(baseApi.util.resetApiState());
      navigate('/');
    } catch (err) {
      toast.error((err as ApiErrorShape)?.msg ?? t('error_generic'));
    } finally {
      setPendingId(null);
    }
  };

  return { select, selecting, pendingId };
};

export default useSelectCompany;
